import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDate } from "../lib/utils";
import { toast } from "sonner";
import {
  Search,
  User,
  Phone,
  Car,
  Building2,
  Calendar,
  AlertCircle,
  ArrowLeft,
  MessageCircle,
  Eye,
  Filter
} from "lucide-react";

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  
  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setQuery(q);
      performSearch(q);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      let params = `q=${encodeURIComponent(searchQuery)}&limit=100`;
      if (!isAdmin() && user?.id) {
        params += `&cre_user_id=${user.id}`;
      }
      
      const data = await apiRequest(`/search?${params}`);
      setResults(data || []);
    } catch (error) {
      toast.error("Search failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.length >= 2) {
      setSearchParams({ q: query });
      performSearch(query);
    }
  };

  const handleResultClick = (item) => {
    if (item.type === "customer") {
      navigate(`/customers/${item.id}`);
    } else if (item.type === "lead") {
      navigate("/leads");
    } else if (item.type === "booking") {
      navigate("/bookings");
    } else if (item.type === "complaint") {
      navigate("/complaints");
    }
  };

  const filteredResults = typeFilter === "all" 
    ? results 
    : results.filter(r => r.type === typeFilter);

  const getTypeIcon = (type) => {
    switch (type) {
      case "customer": return <User className="w-4 h-4 text-blue-600" />;
      case "lead": return <User className="w-4 h-4 text-green-600" />;
      case "booking": return <Calendar className="w-4 h-4 text-purple-600" />;
      case "complaint": return <AlertCircle className="w-4 h-4 text-red-600" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      customer: "bg-blue-100 text-blue-800",
      lead: "bg-green-100 text-green-800",
      booking: "bg-purple-100 text-purple-800",
      complaint: "bg-red-100 text-red-800"
    };
    return <Badge className={`${styles[type] || "bg-gray-100"} text-[10px] uppercase`}>
      {type}
    </Badge>;
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    const styles = {
      due: "bg-yellow-100 text-yellow-800",
      overdue: "bg-orange-100 text-orange-800",
      delayed: "bg-blue-100 text-blue-800",
      no_show: "bg-red-100 text-red-800",
      active: "bg-green-100 text-green-800",
      new_lead: "bg-green-100 text-green-800",
      follow_up: "bg-purple-100 text-purple-800",
      booked: "bg-emerald-100 text-emerald-800",
      scheduled: "bg-blue-100 text-blue-800",
      open: "bg-red-100 text-red-800"
    };
    return <Badge className={`${styles[status] || "bg-gray-100"} text-[10px]`}>
      {status?.replace("_", " ")}
    </Badge>;
  };

  const typeCounts = {
    all: results.length,
    customer: results.filter(r => r.type === "customer").length,
    lead: results.filter(r => r.type === "lead").length,
    booking: results.filter(r => r.type === "booking").length,
    complaint: results.filter(r => r.type === "complaint").length
  };

  return (
    <div className="space-y-6" data-testid="search-results-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">
            Search Results
          </h1>
          <p className="text-muted-foreground text-sm">
            {filteredResults.length} results {query && `for "${query}"`}
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="bento-card">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search name, phone, vehicle, RG NOM, remarks..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" disabled={query.length < 2} data-testid="search-btn">
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <Button
          variant={typeFilter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setTypeFilter("all")}
          className="h-8"
        >
          All ({typeCounts.all})
        </Button>
        <Button
          variant={typeFilter === "customer" ? "default" : "outline"}
          size="sm"
          onClick={() => setTypeFilter("customer")}
          className="h-8"
        >
          <User className="w-3 h-3 mr-1" />
          Customers ({typeCounts.customer})
        </Button>
        <Button
          variant={typeFilter === "lead" ? "default" : "outline"}
          size="sm"
          onClick={() => setTypeFilter("lead")}
          className="h-8"
        >
          <User className="w-3 h-3 mr-1" />
          Leads ({typeCounts.lead})
        </Button>
        <Button
          variant={typeFilter === "booking" ? "default" : "outline"}
          size="sm"
          onClick={() => setTypeFilter("booking")}
          className="h-8"
        >
          <Calendar className="w-3 h-3 mr-1" />
          Bookings ({typeCounts.booking})
        </Button>
        <Button
          variant={typeFilter === "complaint" ? "default" : "outline"}
          size="sm"
          onClick={() => setTypeFilter("complaint")}
          className="h-8"
        >
          <AlertCircle className="w-3 h-3 mr-1" />
          Complaints ({typeCounts.complaint})
        </Button>
      </div>

      {/* Results Table - Desktop */}
      <Card className="bento-card hidden md:block">
        <CardContent className="p-0">
          <div className="data-table-container overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Vehicle / Details</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ED1C24] mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : filteredResults.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {query.length < 2 ? "Enter at least 2 characters to search" : "No results found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredResults.map((item) => (
                    <TableRow 
                      key={`${item.type}-${item.id}`} 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleResultClick(item)}
                      data-testid={`result-row-${item.type}-${item.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTypeIcon(item.type)}
                          {getTypeBadge(item.type)}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatPhone(item.phone)}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          {item.vehicle && (
                            <div className="flex items-center gap-1 text-sm">
                              <Car className="w-3 h-3" />
                              {item.vehicle}
                            </div>
                          )}
                          {item.vehicle_number && (
                            <span className="font-mono text-xs bg-gray-100 px-1 rounded">
                              {item.vehicle_number}
                            </span>
                          )}
                          {item.subject && (
                            <span className="text-sm text-muted-foreground truncate block max-w-[200px]">
                              {item.subject}
                            </span>
                          )}
                          {item.booking_ref && (
                            <span className="font-mono text-xs">Ref: {item.booking_ref}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.branch_name && (
                          <div className="flex items-center gap-1 text-sm">
                            <Building2 className="w-3 h-3" />
                            {item.branch_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(item.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {item.phone && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-green-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.location.href = `tel:${item.phone}`;
                                }}
                              >
                                <Phone className="w-4 h-4" />
                              </Button>
                              <a
                                href={`https://wa.me/91${item.phone?.replace(/\D/g, "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-green-600">
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                              </a>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResultClick(item);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Results Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ED1C24] mx-auto"></div>
          </div>
        ) : filteredResults.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {query.length < 2 ? "Enter at least 2 characters" : "No results found"}
          </div>
        ) : (
          filteredResults.map((item) => (
            <Card 
              key={`${item.type}-${item.id}`}
              className="bento-card"
              onClick={() => handleResultClick(item)}
              data-testid={`result-card-${item.type}-${item.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(item.type)}
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {getTypeBadge(item.type)}
                    {getStatusBadge(item.status)}
                  </div>
                </div>
                
                <div className="space-y-1 text-sm text-muted-foreground mb-3">
                  {item.phone && (
                    <div className="flex items-center gap-1 font-mono">
                      <Phone className="w-3 h-3" />
                      {formatPhone(item.phone)}
                    </div>
                  )}
                  {item.vehicle && (
                    <div className="flex items-center gap-1">
                      <Car className="w-3 h-3" />
                      {item.vehicle}
                      {item.vehicle_number && (
                        <span className="font-mono text-xs bg-gray-100 px-1 rounded ml-1">
                          {item.vehicle_number}
                        </span>
                      )}
                    </div>
                  )}
                  {item.branch_name && (
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {item.branch_name}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {item.phone && (
                    <>
                      <Button
                        size="default"
                        variant="outline"
                        className="flex-1 h-10 text-green-600 border-green-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `tel:${item.phone}`;
                        }}
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Call
                      </Button>
                      <a
                        href={`https://wa.me/91${item.phone?.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          size="default"
                          variant="outline"
                          className="w-full h-10 text-green-600 border-green-200"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          WA
                        </Button>
                      </a>
                    </>
                  )}
                  <Button
                    size="default"
                    variant="outline"
                    className="h-10 px-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResultClick(item);
                    }}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default SearchResultsPage;
